import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, FormControl } from '@mui/material';
import { PUBLIC_PATHS, parsePublicPath, publicPath } from '../utils/publicLocale';

// Current public inner route: the hash route wins when present (e.g. a clean
// path that was mapped to #/privacy), otherwise the clean pathname is parsed.
const currentContext = () => {
    const hash = (window.location.hash || '').replace(/^#/, '');
    const route = hash && hash.startsWith('/')
        ? hash.split('?')[0].replace(/\/+$/, '') || '/'
        : parsePublicPath(window.location.pathname).route;
    return { route, isPublic: PUBLIC_PATHS.includes(route) };
};

const LanguageSwitcher = () => {
    const { i18n } = useTranslation();

    const handleLanguageChange = (event) => {
        const target = event.target.value;
        const { route, isPublic } = currentContext();
        if (isPublic) {
            // On a public page, switch locale by going to the mirror clean URL
            // so the visitor lands on the prerendered per-locale HTML with the
            // correct canonical. A full navigation also resets any hash route.
            window.location.assign(publicPath(target, route));
            return;
        }
        // Private/authenticated app: just switch the in-app language.
        i18n.changeLanguage(target);
    };

    const { isPublic } = currentContext();
    const currentLang = isPublic
        ? parsePublicPath(window.location.pathname).lang
        : (i18n.language ? i18n.language.split('-')[0] : 'fr');

    return (
        <FormControl size="small">
            <Select
                value={currentLang}
                onChange={handleLanguageChange}
                displayEmpty
                // Show a compact 2-letter code (FR/EN) as the selected value so
                // the switcher stays narrow — the full "Français"/"English"
                // labels made the mobile nav row wider than the viewport and
                // caused horizontal page overflow. The dropdown still lists the
                // full names.
                renderValue={(val) => (val === 'en' ? 'EN' : 'FR')}
                sx={{
                    borderRadius: '999px',
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--brown-900)',
                    background: 'transparent',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--cream-200)',
                        borderWidth: '1.5px'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--orange-600)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--orange-600)'
                    }
                }}
            >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
            </Select>
        </FormControl>
    );
};

export default LanguageSwitcher;
